import { flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useCallback, useEffect, useState } from 'react';

// TODO: Take a run types or io-ts structural validation function for deserialization

/** Provide a hook that presents a useState style API over a local storage
 * backed value. */
const useLocalStorage = <Value>(key: string, defaultValue: Value) => {
  const [value, setValue] = useState<Value>(defaultValue);

  const setLocalValue = useCallback(
    (v: Value) => localStorage.setItem(key, JSON.stringify(v)),
    [key]
  );

  const getLocalValue = useCallback(
    () =>
      pipe(
        localStorage.getItem(key),
        O.fromNullable,
        O.chain(flow(JSON.parse, O.fromNullable)),
        O.getOrElse(() => defaultValue)
      ),
    [key, defaultValue]
  );

  // Initialisation
  useEffect(() => setValue(getLocalValue()), [setValue, getLocalValue]);

  // Handle when storage changes
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== key) return null;
      setValue(getLocalValue());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, setValue, getLocalValue]);

  const update = useCallback(
    (v: Value) => {
      setLocalValue(v);
      setValue(v);
    },
    [setLocalValue, setValue]
  );

  return [value, update] as const;
};

export default useLocalStorage;
